import { Link } from "react-router-dom";
import { ShieldCheck, Factory, Cpu, Ruler, Check, ArrowRight, ArrowUpRight, IndianRupee } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Faq from "@/components/Faq";

const PACKAGES = [
  {
    id: "modular-kitchen",
    tag: "Modular Kitchens",
    title: "Modular Kitchen — L, U, Parallel & Island",
    desc: "Factory-manufactured modular kitchens in BWR ply, HDHMR or marine ply carcasses with Hettich or Blum hardware. Countertops in Italian marble, quartz, granite, Corian or oak butcher-block. Live 3D configurator on the site.",
    unit: "3 BHK reference",
    low: 180000,
    high: 650000,
  },
  {
    id: "2bhk",
    tag: "Residential — 2 BHK",
    title: "Full-home 2 BHK interiors",
    desc: "Complete home turnkey. Modular kitchen + all wardrobes + false ceiling + lighting + wall décor + TV / entertainment units + painting + civil / electrical / plumbing coordination.",
    unit: "package",
    low: 650000,
    high: 1400000,
    duration: "45 – 60 days",
  },
  {
    id: "3bhk",
    tag: "Residential — 3 BHK",
    title: "Full-home 3 BHK interiors",
    desc: "Every 2 BHK inclusion plus additional bedroom + dining + study / home-office joinery and richer finish tiers on cabinetry and lighting.",
    unit: "package",
    low: 1000000,
    high: 2200000,
    duration: "60 – 75 days",
  },
  {
    id: "4bhk",
    tag: "Residential — 4 BHK & duplex",
    title: "Full-home 4 BHK / duplex interiors",
    desc: "Full 4-bedroom homes and duplex apartments. Includes staircase joinery when applicable, dedicated home office + puja room + kids' rooms, premium finish tiers.",
    unit: "package",
    low: 1600000,
    high: 3500000,
    duration: "75 – 100 days",
  },
  {
    id: "wardrobes",
    tag: "Wardrobes & storage",
    title: "Bespoke wardrobes & storage",
    desc: "Sliding / hinged wardrobes, walk-in closets, TV storage walls, foyer cabinets, loft storage — all soft-close, all measured to your room. Sold per running foot.",
    unit: "per rft",
    low: 1200,
    high: 4200,
  },
  {
    id: "commercial",
    tag: "Turnkey Commercial",
    title: "Office, café, restaurant, retail, salon fit-outs",
    desc: "End-to-end turnkey commercial interiors. Civil + MEP coordination, workstations / seating / bar / POS + branded feature walls, hospitality-grade finishes.",
    unit: "per sqft",
    low: 1800,
    high: 6000,
    duration: "30 – 90 days",
  },
  {
    id: "luxury",
    tag: "Luxury & bungalows",
    title: "Luxury villas, bungalows & premium duplexes",
    desc: "Highest tier — Italian marble flooring, imported hardware, veneer feature walls, home automation, dedicated wine / cigar rooms, indoor water features.",
    unit: "package",
    low: 3500000,
    high: 5000000,
  },
];

const WARRANTY = [
  { years: "10", label: "Modular carcass & shell", body: "Full replacement warranty on the modular kitchen and wardrobe carcasses — plywood / HDHMR / marine-ply core against warping, delamination and swelling." },
  { years: "5",  label: "Hardware & fittings", body: "Hettich, Blum or Ebco channels, hinges, tandem drawers, wire baskets and lift-up mechanisms carry a full 5-year service warranty." },
  { years: "1",  label: "Civil & finishing", body: "One-year comprehensive service on installation — including painting, polishing, false ceiling, electrical, plumbing and site-installed civil work." },
];

const CAPABILITIES = [
  { icon: Factory, label: "In-house factory", body: "ZEBO manufactures all modular carcasses in our own factory in West Bengal. Machined cuts, edge-banding and CNC drilling ensure factory-grade precision no on-site carpenter can match." },
  { icon: Cpu,     label: "Live 3D configurator", body: "Browser-based real-time 3D. Swap Italian marble, quartz, walnut veneer, brass panelling live and see the total ₹ update instantly. AR-view on your phone." },
  { icon: Ruler,   label: "Digital measurement", body: "Laser distometers and digital site-survey templates ensure every cabinet is manufactured to the exact millimetre of your room." },
  { icon: ShieldCheck, label: "Named designer + PM", body: "One dedicated senior designer and one project manager on every job — same faces from BOQ sign-off to keys-in-hand handover, no vendor hand-offs." },
];

const format = (n) => "₹" + (n >= 100000 ? (n / 100000).toFixed(n % 100000 === 0 ? 0 : 1) + " L" : n.toLocaleString("en-IN"));

export default function Pricing() {
  const faqItems = [
    { q: "How much does a turnkey interior project cost in Kolkata?", a: "ZEBO turnkey interior projects range from ₹2 Lakh (a single modular kitchen refresh) up to ₹50 Lakh (a full 4BHK luxury home). Indicative bands: 2BHK ₹6.5 L – ₹14 L · 3BHK ₹10 L – ₹22 L · 4BHK / duplex ₹16 L – ₹35 L · Luxury villa ₹35 L – ₹50 L+. Final BOQ is issued after a free site visit." },
    { q: "How much does a modular kitchen cost in Kolkata?", a: "For a standard 3 BHK reference kitchen ZEBO modular kitchens cost ₹1.8 Lakh to ₹6.5 Lakh — the range depends on layout (L-shape, U-shape, Parallel or Island), core material (BWR plywood, HDHMR or marine ply), finish tier (laminate, acrylic, PU lacquer or wood veneer) and countertop choice (Kashmir granite, quartz, Italian marble, Corian). Priced live in the 3D configurator." },
    { q: "What warranty does ZEBO offer on modular kitchens and wardrobes?", a: "Ten years on modular carcasses, five years on hardware (Hettich / Blum / Ebco channels, hinges, drawer runners), and one year comprehensive service on all civil and site-installed finishing — including painting, polishing, false ceiling, electrical and plumbing." },
    { q: "Does ZEBO manufacture in-house or outsource?", a: "In-house. ZEBO owns and operates its own factory in West Bengal — CNC drilling, machined cuts and edge-banding are done under one roof. That's how we hit factory-grade precision no on-site carpenter can match, and how the 10-year carcass warranty is credible." },
    { q: "Is the initial design consultation free?", a: "Yes. The first consultation — site visit, requirement mapping, mood board and rough budget — is completely free, with no obligation. Charges only apply after you sign a detailed BOQ." },
    { q: "How long does a full home interior project take?", a: "Standard timelines end-to-end (BOQ sign-off to handover): 2BHK 45 – 60 days · 3BHK 60 – 75 days · 4BHK / duplex 75 – 100 days · luxury villas 100 – 140 days. Factory-manufactured modules run in parallel with on-site civil work, which is how we hit these timelines without cutting corners." },
    { q: "What areas does ZEBO serve?", a: "West Bengal (Kolkata HQ + Howrah, Durgapur, Asansol, Siliguri, Kharagpur), Jharkhand (Ranchi, Jamshedpur, Dhanbad, Bokaro), Odisha (Bhubaneswar, Cuttack, Rourkela, Puri) and Bihar (Patna, Gaya, Muzaffarpur, Bhagalpur). Complimentary site visits within 25 km of our Kolkata Chambers Mall studio; scheduled visits at each milestone for sites further afield." },
    { q: "Can I see a live 3D preview and AR before signing?", a: "Yes. Every kitchen can be designed by you in the browser-based live 3D configurator. On mobile you can tap 'View in AR' and place the exact kitchen — cabinets, countertop, floor — inside your real room via your phone camera (Android Scene Viewer / iOS Quick Look) before a single bolt is turned." },
  ];

  return (
    <main data-testid="pricing-page" className="pt-32 pb-24 bg-[#F9F8F6] relative z-10">
      <Seo
        title="Interior Design Prices Kolkata · Packages & Warranty — ZEBO"
        description="ZEBO transparent price list: modular kitchens ₹1.8–6.5L, 2BHK ₹6.5–14L, 3BHK ₹10–22L, 4BHK ₹16–35L, commercial ₹1,800–6,000/sqft. 10-year warranty, in-house factory, live 3D."
        path="/pricing"
      />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        {/* Hero */}
        <Reveal>
          <p className="overline text-[#8B0000] mb-4 flex items-center gap-2"><IndianRupee size={14} /> Packages · Pricing · Warranty</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#143D63] leading-[0.98] max-w-4xl">
            Honest ZEBO pricing, published up-front.
          </h1>
          <p className="text-[#143D63]/70 mt-6 max-w-2xl text-base sm:text-lg">
            Interior firms in Kolkata almost never publish prices. We do. Below are the exact ranges we quote — modular kitchens ₹1.8 – 6.5 Lakh, full-home projects ₹2 – 50 Lakh — plus the warranty and factory capabilities that back them.
          </p>
        </Reveal>

        {/* Packages grid */}
        <div data-testid="pricing-grid" className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <div data-testid={`pricing-card-${p.id}`} className="border border-[#143D63]/15 bg-white p-7 h-full flex flex-col group hover:border-[#F8C21C] transition-colors">
                <p className="overline text-[#8B0000] text-[10px]">{p.tag}</p>
                <h3 className="font-serif text-2xl text-[#143D63] mt-2 mb-3 leading-tight">{p.title}</h3>
                <p className="text-sm text-[#143D63]/70 leading-relaxed flex-1">{p.desc}</p>
                <div className="mt-5 pt-5 border-t border-[#143D63]/10">
                  <p className="text-[11px] uppercase tracking-wider text-[#143D63]/50">{p.unit === "per sqft" ? "Per square foot" : p.unit === "per rft" ? "Per running foot" : "Package range"}</p>
                  <p className="font-serif text-3xl text-[#143D63] mt-1">
                    {format(p.low)} <span className="text-[#F8C21C]">–</span> {format(p.high)}
                  </p>
                  {p.duration && <p className="text-xs text-[#143D63]/60 mt-2">Timeline · {p.duration}</p>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="text-[#143D63]/60 text-xs mt-6 max-w-3xl">
            All ranges are indicative and reflect standard Kolkata scopes. Final Bill-of-Quantities is issued after a free site visit and depends on measured area, chosen core material, finish tier, hardware and site-specific civil work.
          </p>
        </Reveal>

        {/* Warranty */}
        <section data-testid="warranty-section" className="mt-24 border-t border-[#143D63]/15 pt-16">
          <Reveal>
            <p className="overline text-[#8B0000] mb-3 flex items-center gap-2"><ShieldCheck size={14} /> Written warranty terms</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#143D63] leading-[1] max-w-3xl">
              10 · 5 · 1 — the ZEBO warranty.
            </h2>
            <p className="text-[#143D63]/70 mt-5 max-w-2xl">
              A modular kitchen or wardrobe is a decade-long installation. Our written warranty policy is designed to make that decade worry-free.
            </p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-[#143D63]/15 border border-[#143D63]/15">
            {WARRANTY.map((w, i) => (
              <Reveal key={w.years} delay={i * 0.08} className="bg-white">
                <div className="p-8 h-full flex flex-col">
                  <p className="font-serif text-7xl text-[#F8C21C] leading-none">{w.years}<span className="text-3xl text-[#8B0000] ml-1">yr</span></p>
                  <h3 className="font-serif text-2xl text-[#143D63] mt-6 mb-2">{w.label}</h3>
                  <p className="text-sm text-[#143D63]/70 leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Manufacturing / capabilities */}
        <section data-testid="capabilities-section" className="mt-24 border-t border-[#143D63]/15 pt-16">
          <Reveal>
            <p className="overline text-[#8B0000] mb-3 flex items-center gap-2"><Factory size={14} /> How we deliver</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#143D63] leading-[1] max-w-3xl">
              In-house factory. Digital estimation. Named team.
            </h2>
            <p className="text-[#143D63]/70 mt-5 max-w-3xl">
              ZEBO owns and operates its own manufacturing workshop in West Bengal. Every modular carcass — kitchen, wardrobe, TV wall, home-office joinery — is CNC-machined, edge-banded and quality-checked under our roof before it reaches your site. Design happens in our browser-based live 3D configurator with AR preview on mobile, so what you see on screen is what gets manufactured to the millimetre.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CAPABILITIES.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.label} delay={i * 0.08}>
                  <div className="border border-[#143D63]/15 bg-white p-6 h-full">
                    <div className="w-11 h-11 bg-[#143D63] text-[#F8C21C] flex items-center justify-center mb-4"><Icon size={20} /></div>
                    <h3 className="font-serif text-xl text-[#143D63] mb-2">{c.label}</h3>
                    <p className="text-sm text-[#143D63]/70 leading-relaxed">{c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-24 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 bg-gradient-to-br from-[#143D63] to-[#0F2E4C] text-[#F9F8F6] p-10">
            <p className="overline text-[#F8C21C] mb-3">Free consultation · Free 3D preview · Free BOQ</p>
            <h2 className="font-serif text-4xl font-light leading-tight max-w-xl">Get the exact price for your space in 24 hours.</h2>
            <p className="text-[#F9F8F6]/75 mt-4 max-w-lg text-sm">Book a free site visit — we'll measure, discuss scope, hand you a photorealistic 3D render and a fully priced BOQ within a working day.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/consultation" data-testid="pricing-book-btn" className="inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-6 py-3.5 font-bold text-sm hover:bg-[#F9F8F6] transition-colors">Book a free visit <ArrowUpRight size={15} /></Link>
              <Link to="/configurator" data-testid="pricing-configurator-btn" className="inline-flex items-center gap-2 border border-[#F8C21C] text-[#F8C21C] px-6 py-3.5 font-bold text-sm hover:bg-[#F8C21C] hover:text-[#143D63] transition-colors">Design in 3D now <ArrowRight size={15} /></Link>
            </div>
          </div>
          <div className="lg:col-span-4 bg-white border border-[#143D63]/15 p-8">
            <p className="overline text-[#8B0000] mb-3">Included in every project</p>
            <ul className="space-y-3 text-sm text-[#143D63]">
              {["Site visit + measurement", "Mood board + BOQ", "Photorealistic 3D + 360° render", "Named designer + PM", "10 · 5 · 1 written warranty", "Post-handover snagging service"].map((x) => (
                <li key={x} className="flex items-start gap-2"><Check className="text-[#F8C21C] shrink-0 mt-0.5" size={16} />{x}</li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      {/* FAQ — GEO Q&A format */}
      <Faq
        eyebrow="Direct answers, straight up"
        title="Pricing and warranty questions we're asked every week"
        items={faqItems}
        id="pricing-faq-schema"
      />
    </main>
  );
}
